import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { HeaderCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import {
	BodyRow,
	DefaultTable,
	HeaderRow,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useEffect, useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';
import { SecurityRowModal } from './SecurityRowModal/SecurityRowModal';
import { useSecuritiesTableController } from './useSecuritiesTableController';
import { SecuritiesForm } from './useSecuritiesTableController.types';

interface Props {
	className?: string;
	securitiesForm: UseFormReturn<SecuritiesForm, unknown>;
	showFairMarketValuePerUnitColumn?: boolean;
	showNumericalColumn?: boolean;
}

const SecuritiesTableModal = ({
	securitiesForm,
	className,
	showFairMarketValuePerUnitColumn,
	showNumericalColumn,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	const { securitiesFields, onRemoveSecurityRow, onAddSecurityRow } =
		useSecuritiesTableController(securitiesForm);
	const [fieldsLength, setFieldsLength] = useState(true);
	useEffect(() => {
		setFieldsLength(securitiesFields?.length === 0);
	}, [securitiesFields]);

	return (
		<div className={className}>
			<FormProvider {...securitiesForm}>
				<DefaultTable maxSize withEditableRows>
					<HeaderRow>
						{showNumericalColumn && <HeaderCell size={0.5}>#</HeaderCell>}
						<HeaderCell customSize="0.5fr">{tCommonUi('securities.type')}</HeaderCell>
						<HeaderCell customSize="0.7fr">
							{tCommonUi('securities.entity_name')}
						</HeaderCell>
						<HeaderCell customSize="0.4fr" childrenAlign="right">
							{tCommonUi('securities.votes_per_share')}
						</HeaderCell>
						<HeaderCell customSize="0.4fr" childrenAlign="right">
							{tCommonUi('securities.nominal_value')}
						</HeaderCell>
						<HeaderCell customSize="0.4fr" childrenAlign="center">
							{tCommonUi('securities.subject_to_vesting')}
						</HeaderCell>
						{showFairMarketValuePerUnitColumn && (
							<HeaderCell customSize="0.4fr" childrenAlign="right">
								{tCommonUi('securities.value_per_unit')}
							</HeaderCell>
						)}
						<HeaderCell size={2.1} />
					</HeaderRow>
					<BodyRow>
						{securitiesFields?.map((security, index) => (
							<SecurityRowModal
								key={security.id}
								fieldIndex={index}
								onRemove={onRemoveSecurityRow(security.id)}
								showNumericalColumn={showNumericalColumn}
								showFairMarketValuePerUnitColumn={showFairMarketValuePerUnitColumn}
							/>
						))}
					</BodyRow>
				</DefaultTable>
				<UIElementIdentifier identifier={TEST_ELEMENT_IDS.ADD_SECURITY_BUTTON}>
					<AddSecurityButtonWrapper isEmpty={fieldsLength}>
						<AddSecurityButton
							onClick={onAddSecurityRow}
							iconSize="lg"
							icon="add-circle"
							iconPlacement="left"
							size="medium"
							variant="text"
						>
							{tCommonUi('common_buttons.add_another')}
						</AddSecurityButton>
					</AddSecurityButtonWrapper>
				</UIElementIdentifier>
			</FormProvider>
		</div>
	);
};
const AddSecurityButtonWrapper = styled.div<{ isEmpty: boolean }>``;
const AddSecurityButton = styled(Button)`
	margin-top: 1em;
`;

const Styled = styled(SecuritiesTableModal)`
	overflow: auto;
	${DefaultTable} {
		overflow: visible;
	}
`;

export { Styled as SecurityTableModal };
